import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  getLectureById,
  createLecture,
  updateLecture,
} from '../api/lectureApi'
import { getMasterOptionItems } from '../../../admission/students/api/mastersApi'
import api from '../../../../api/client'
import { appToast } from '../../../../components/AppToast'

export function generateAcademicYears(startYear = 2000, endYear = 2038) {
  const years = []
  for (let y = endYear; y >= startYear; y--) {
    const nextY = String(y + 1).slice(-2)
    const val = `${y}-${nextY}`
    years.push({ value: val, label: val })
  }
  return years
}

const ALL_ACADEMIC_YEARS = generateAcademicYears()

const DEFAULT_FORM = {
  academicYear: '2026-27',
  ignoreDuplication: false,
  date: new Date().toISOString().slice(0, 10),
  tillDate: new Date().toISOString().slice(0, 10),
  planHour: '10',
  planMinute: '00',
  planAmPm: 'AM',
  tillHour: '10',
  tillMinute: '00',
  tillAmPm: 'AM',
  company: '',
  companyScope: 'Single',
  branch: '',
  branchScope: 'Single',
  course: '',
  batch: '',
  batchScope: 'Single',
  subject: '',
  schoolCollege: '',
  schoolCollegeScope: 'Single',
  chapterNo: 0,
  topic: '',
  faculty: '',
  extraLecture: false,
  location: '',
  videoUrl: '',
  createdBy: 'Admin (System)',
  updatedBy: '—',
}

export default function useLectureEntryScreen() {
  const navigate = useNavigate()
  const { id: paramId } = useParams()
  const [searchParams] = useSearchParams()
  const id = paramId || searchParams.get('id')
  const isEdit = Boolean(id)

  const [form, setForm] = useState(DEFAULT_FORM)
  const [loading, setLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Real Dynamic Master Options from Backend (Rule 90)
  const [masterOptions, setMasterOptions] = useState({
    courses: [],
    subjects: [],
    branches: [],
    batches: [],
    faculty: [],
    locations: [],
    companies: [],
    schools: [],
  })

  const [extraOptions, setExtraOptions] = useState({
    courses: [],
    subjects: [],
    branches: [],
    batches: [],
    faculty: [],
    locations: [],
    companies: [],
    schools: [],
  })

  // Load master options on mount
  useEffect(() => {
    async function loadMasterOptions() {
      try {
        const [cItems, bItems, sItems, brItems, compItems, scItems, clItems] = await Promise.all([
          getMasterOptionItems('courses'),
          getMasterOptionItems('batches'),
          getMasterOptionItems('subjects'),
          getMasterOptionItems('branches'),
          getMasterOptionItems('institutes'),
          getMasterOptionItems('schools'),
          getMasterOptionItems('classrooms'),
        ])

        let facItems = []
        try {
          const { data: facData } = await api.get('/options/acheads', { params: { group_id: 33, page: 1, page_size: 100 } })
          facItems = facData?.items || []
        } catch (err) {
          console.error('Error loading faculties from /options/acheads:', err)
        }

        setMasterOptions({
          courses: Array.isArray(cItems) ? cItems.map((c) => ({ value: c.name || String(c.id), label: c.name })) : [],
          batches: Array.isArray(bItems) ? bItems.map((b) => ({ value: b.name || String(b.id), label: b.name })) : [],
          subjects: Array.isArray(sItems) ? sItems.map((s) => ({ value: s.name || String(s.id), label: s.name })) : [],
          branches: Array.isArray(brItems) ? brItems.map((b) => ({ value: b.name || String(b.id), label: b.name })) : [],
          companies: Array.isArray(compItems) ? compItems.map((c) => ({ value: c.name || String(c.id), label: c.name })) : [],
          schools: Array.isArray(scItems) ? scItems.map((s) => ({ value: s.name || String(s.id), label: s.name })) : [],
          locations: Array.isArray(clItems) ? clItems.map((r) => ({ value: r.name || String(r.id), label: r.name })) : [],
          faculty: Array.isArray(facItems) ? facItems.map((f) => ({ value: f.name || f.label || String(f.id), label: f.name || f.label })) : [],
        })
      } catch (err) {
        console.error('Failed to load master options for lecture entry:', err)
      }
    }
    loadMasterOptions()
  }, [])

  function parseTimeParts(raw) {
    if (!raw) return { hour: '10', min: '00', ampm: 'AM' }
    const str = String(raw).trim()
    const m = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i)
    if (m) {
      let h = parseInt(m[1], 10)
      const min = m[2]
      let ampm = m[3] ? m[3].toUpperCase() : null
      if (!ampm) {
        if (h >= 12) {
          ampm = 'PM'
          if (h > 12) h -= 12
        } else {
          ampm = 'AM'
          if (h === 0) h = 12
        }
      } else {
        if (h === 0) h = 12
      }
      return {
        hour: String(h).padStart(2, '0'),
        min: min,
        ampm: ampm || 'AM',
      }
    }
    return { hour: '10', min: '00', ampm: 'AM' }
  }

  function extractDateString(raw) {
    if (!raw) return new Date().toISOString().slice(0, 10)
    const str = String(raw).trim()
    return str.slice(0, 10)
  }

  useEffect(() => {
    async function fetchRecord() {
      if (!isEdit) {
        setForm(DEFAULT_FORM)
        return
      }
      try {
        setLoading(true)
        const existing = await getLectureById(id)
        if (existing) {
          const pTime = parseTimeParts(existing.p_from || existing.startTime)
          const tTime = parseTimeParts(existing.p_till || existing.endTime)

          const cName = existing.course_name || existing.course || ''
          const sName = existing.subject_name || existing.subject || ''
          const bNames = existing.branch_names || existing.branch || ''
          const batchNames = existing.batch_names || existing.batch || ''
          const fName = existing.faculty_name || existing.faculty || ''
          const roomName = existing.classroom_name || existing.classroom || existing.location || ''
          const compName = existing.institute_name || existing.company || ''
          const schoolName = existing.school_name || existing.schoolCollege || ''

          setExtraOptions({
            courses: cName ? [{ value: cName, label: cName }] : [],
            subjects: sName ? [{ value: sName, label: sName }] : [],
            branches: bNames ? [{ value: bNames, label: bNames }] : [],
            batches: batchNames ? [{ value: batchNames, label: batchNames }] : [],
            faculty: fName ? [{ value: fName, label: fName }] : [],
            locations: roomName ? [{ value: roomName, label: roomName }] : [],
            companies: compName ? [{ value: compName, label: compName }] : [],
            schools: schoolName ? [{ value: schoolName, label: schoolName }] : [],
          })

          setForm({
            academicYear: existing.academic_year || existing.aYear || existing.academicYear || '2026-27',
            ignoreDuplication: Boolean(existing.ignore_duplication || existing.ignoreDuplication),
            date: extractDateString(existing.p_date || existing.date),
            tillDate: extractDateString(existing.till_date || existing.tillDate || existing.p_date || existing.date),
            planHour: pTime.hour,
            planMinute: pTime.min,
            planAmPm: pTime.ampm,
            tillHour: tTime.hour,
            tillMinute: tTime.min,
            tillAmPm: tTime.ampm,
            company: compName,
            companyScope: existing.company_scope || existing.companyScope || 'Single',
            branch: bNames,
            branchScope: existing.branch_scope || existing.branchScope || 'Single',
            course: cName,
            batch: batchNames,
            batchScope: existing.batch_scope || existing.batchScope || 'Single',
            subject: sName,
            schoolCollege: schoolName,
            schoolCollegeScope: existing.school_college_scope || existing.schoolCollegeScope || 'Single',
            chapterNo: existing.chapter_no || existing.chapterNo || 0,
            topic: existing.topic || '',
            faculty: fName,
            extraLecture: Boolean(existing.extra_lecture || existing.extraLecture),
            location: roomName,
            videoUrl: existing.video_url || existing.videoUrl || existing.meetingUrl || '',
            createdBy: existing.created_by_name || existing.createdBy || 'Admin',
            updatedBy: existing.updated_by_name || existing.updatedBy || new Date().toLocaleDateString(),
          })
        } else {
          appToast.error('Lecture record not found.')
          navigate('/lectures/list')
        }
      } catch (e) {
        console.error('Failed to load lecture', e)
        appToast.error('Failed to load lecture.')
      } finally {
        setLoading(false)
      }
    }
    fetchRecord()
  }, [id, isEdit, navigate])

  function patchForm(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  async function handleSave(continueEditing = false) {
    if (!form.date) {
      appToast.error('Please enter a Date.')
      return
    }
    if (!form.company) {
      appToast.error('Please select a Company.')
      return
    }
    if (!form.course) {
      appToast.error('Please select a Course.')
      return
    }
    if (!form.subject) {
      appToast.error('Please select a Subject.')
      return
    }
    if (!form.faculty) {
      appToast.error('Please assign a Faculty.')
      return
    }

    try {
      setIsSaving(true)
      const facultyAvatar = (form.faculty || 'FC').slice(0, 2).toUpperCase()

      const startTime = `${form.planHour}:${form.planMinute} ${form.planAmPm}`
      const endTime = `${form.tillHour}:${form.tillMinute} ${form.tillAmPm}`

      const payload = {
        ...form,
        startTime,
        endTime,
        facultyAvatar,
        classroom: form.location,
        meetingUrl: form.videoUrl,
        status: form.status || 'Scheduled',
      }

      if (isEdit) {
        await updateLecture(id, payload)
        appToast.success('Lecture updated successfully!')
      } else {
        await createLecture(payload)
        appToast.success('Lecture created successfully!')
      }

      if (continueEditing) {
        // Save & Continue: Advance time to next slot and carry over common fields
        let nextHour = parseInt(form.tillHour, 10) + 1
        let nextAmPm = form.tillAmPm
        if (nextHour > 12) {
          nextHour = 1
        }
        setForm((prev) => ({
          ...prev,
          planHour: prev.tillHour,
          planMinute: prev.tillMinute,
          planAmPm: prev.tillAmPm,
          tillHour: String(nextHour).padStart(2, '0'),
          tillMinute: prev.tillMinute,
          tillAmPm: nextAmPm,
          faculty: '',
          subject: '',
          topic: '',
          chapterNo: 0,
        }))
      } else {
        navigate('/lectures/list')
      }
    } catch (e) {
      console.error('Failed to save lecture', e)
      appToast.error('Failed to save lecture.')
    } finally {
      setIsSaving(false)
    }
  }

  function mergeOptionList(baseItems, extraItems = []) {
    const map = new Map()
    for (const item of extraItems) {
      if (item && item.value) {
        map.set(String(item.value), item)
      }
    }
    for (const item of baseItems) {
      if (item && item.value && !map.has(String(item.value))) {
        map.set(String(item.value), item)
      }
    }
    return Array.from(map.values())
  }

  return {
    state: {
      form,
      isEdit,
      id,
      loading,
      isSaving,
      options: {
        academicYears: ALL_ACADEMIC_YEARS,
        hours: Array.from({ length: 12 }, (_, i) => {
          const val = String(i + 1).padStart(2, '0')
          return { value: val, label: val }
        }),
        minutes: [
          { value: '00', label: '00' },
          { value: '15', label: '15' },
          { value: '30', label: '30' },
          { value: '45', label: '45' },
        ],
        ampm: [
          { value: 'AM', label: 'AM' },
          { value: 'PM', label: 'PM' },
        ],
        companies: mergeOptionList(masterOptions.companies, extraOptions.companies),
        branches: mergeOptionList(masterOptions.branches, extraOptions.branches),
        courses: mergeOptionList(masterOptions.courses, extraOptions.courses),
        batches: mergeOptionList(masterOptions.batches, extraOptions.batches),
        subjects: mergeOptionList(masterOptions.subjects, extraOptions.subjects),
        schools: mergeOptionList(masterOptions.schools, extraOptions.schools),
        faculty: mergeOptionList(masterOptions.faculty, extraOptions.faculty),
        locations: mergeOptionList(masterOptions.locations, extraOptions.locations),
      },
    },
    actions: {
      patchForm,
      handleSave,
    },
  }
}
